import { FC, useEffect, useState, useRef } from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  ListItemIcon,
  IconButton,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { ListingResource } from '../../../system/field';
import {
  SaveButton,
  SidePanel,
} from '../../../components/create/assistant/components/ListingComponents';
import { useSidePanel } from '../../../contexts/SidePanelContext';
import { useFieldUpdater, useListingStore } from '@brightmls/nextgen.lae.flc';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import isEqual from 'lodash/isEqual';

interface ObjectDetectionPanelProps {
  isOpen: boolean;
  setIsObjectPanelOpen: (isOpen: boolean) => void;
  listingKey: string;
  isPendingGeneration: () => boolean;
  pendingGenerations: string[];
  progress: {
    done: number;
    total: number;
  };
  objectResults: { [key: string]: any[] };
  setObjectDetectionResults: (results: { [key: string]: any[] }) => void;
}

export const ObjectDetectionPanel: FC<ObjectDetectionPanelProps> = ({
  isOpen,
  setIsObjectPanelOpen,
  isPendingGeneration,
  pendingGenerations: _pendingGenerations,
  progress,
  objectResults,
  setObjectDetectionResults,
  listingKey,
}) => {
  const { CustomDrawer } = useSidePanel();
  const updateField = useFieldUpdater();

  // State to track which labels are expanded
  const [expandedLabels, setExpandedLabels] = useState<Set<string>>(new Set());

  // State to track the selected fields data grouped by field key
  const [selectedFieldsData, setSelectedFieldsData] = useState<{
    [fieldKey: string]: string[];
  }>({});

  // State to track the initial selected fields data for comparison
  const [initialSelectedFieldsData, setInitialSelectedFieldsData] = useState<{
    [fieldKey: string]: string[];
  }>({});

  const prevResultsRef = useRef<object | null>(null);

  /* -----------------------------------------------------------
   * Persist and rehydrate object-detection results in localStorage
   * ----------------------------------------------------------- */
  useEffect(() => {
    if (!listingKey) return;

    // Load any saved results the first time this component is used for a listing.
    try {
      const saved = localStorage.getItem(`objectDetection_${listingKey}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          setObjectDetectionResults(parsed);
        }
      }
    } catch (error) {
      console.error('Failed to parse saved object-detection results', error);
    }
  }, [listingKey]);

  // Persist whenever results change
  useEffect(() => {
    if (!listingKey) return;

    try {
      if (objectResults && Object.keys(objectResults).length) {
        localStorage.setItem(
          `objectDetection_${listingKey}`,
          JSON.stringify(objectResults),
        );
      }
    } catch (error) {
      console.error('Failed to save object-detection results', error);
    }
  }, [objectResults, listingKey]);

  // Handler to toggle label expansion
  const handleToggleLabel = (label: string) => {
    setExpandedLabels((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(label)) {
        newExpanded.delete(label);
      } else {
        newExpanded.add(label);
      }
      return newExpanded;
    });
  };

  const handleClosePanel = () => {
    setIsObjectPanelOpen(false);
  };

  // Handler to toggle field selection
  const handleUpdateFields = (label: string, fieldKey: string) => {
    // Update the selectedFieldsData to group by fieldKey
    setSelectedFieldsData((prev) => {
      const currentLabels = prev[fieldKey] ?? [];
      const isCurrentlySelected = currentLabels.includes(label);

      let nextState: { [key: string]: string[] };

      if (isCurrentlySelected) {
        const updatedLabels = currentLabels.filter((item) => item !== label);

        if (updatedLabels.length === 0) {
          const { [fieldKey]: _removed, ...rest } = prev;
          nextState = rest;
        } else {
          nextState = { ...prev, [fieldKey]: updatedLabels };
        }
      } else {
        nextState = {
          ...prev,
          [fieldKey]: [...currentLabels, label],
        };
      }

      return nextState;
    });
  };

  useEffect(() => {
    // skip if nothing actually changed
    if (isEqual(prevResultsRef.current, objectResults)) return;
    prevResultsRef.current = objectResults;

    const initialSelectedData: Record<string, string[]> = {};

    // Get the current state directly from the store for dynamic field access
    const state = useListingStore.getState();

    Object.entries(objectResults).forEach(([label, results]) => {
      results.forEach((result) => {
        const existingValues = state.getResourceFieldValue(
          ListingResource.Property,
          result.fieldKey,
          [],
        );

        // If the label already exists in the field, pre-select it
        if (existingValues.includes(label)) {
          if (!initialSelectedData[result.fieldKey]) {
            initialSelectedData[result.fieldKey] = [];
          }
          if (!initialSelectedData[result.fieldKey].includes(label)) {
            initialSelectedData[result.fieldKey].push(label);
          }
        }
      });
    });

    setSelectedFieldsData(initialSelectedData);
    setInitialSelectedFieldsData(initialSelectedData);
  }, [objectResults]);

  const renderObjectPanel = () => {
    return (
      <SidePanel>
        <Box>
          <Typography variant="body1">
            Listed below are the objects detected in the photos uploaded. Select
            if you want to add them to your dropdown(s).
          </Typography>
          {isPendingGeneration() && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                my: 2,
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  my: 2,
                  gap: 2,
                }}
              >
                <CircularProgress size={24} />
                <Typography variant="body1">
                  Detecting objects with AI...
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: '100%', mr: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(progress.done / progress.total) * 100}
                  />
                </Box>
                <Box sx={{ minWidth: 35 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary' }}
                  >{`${Math.round((progress.done / progress.total) * 100)}%`}</Typography>
                </Box>
              </Box>
            </Box>
          )}
          {!isPendingGeneration() &&
            Object.keys(objectResults).length === 0 && (
              <Typography
                variant="body2"
                sx={{ mt: 2, color: 'text.secondary' }}
              >
                No objects detected.
              </Typography>
            )}
          {Object.keys(objectResults).length > 0 && (
            <List component="nav" sx={{ mt: 2 }}>
              {Object.entries(objectResults).map(([label, results]) => (
                <Box key={label}>
                  <ListItem disablePadding>
                    <ListItemButton
                      sx={{
                        gap: '4px',
                        color: 'text.primary',
                        borderRadius: '8px',
                      }}
                      onClick={() => handleToggleLabel(label)}
                    >
                      <ListItemIcon sx={{ minWidth: 'auto' }}>
                        {expandedLabels.has(label) ? (
                          <ExpandLess sx={{ color: 'text.primary' }} />
                        ) : (
                          <ExpandMore sx={{ color: 'text.primary' }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={label}
                        slotProps={{
                          primary: {
                            variant: 'body1',
                            sx: { fontWeight: 700 },
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                  <Collapse
                    in={expandedLabels.has(label)}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List component="div" disablePadding>
                      {results.map((result, index) => (
                        <ListItem
                          key={`${result.fieldKey}-${index}`}
                          sx={{ pl: '28px', gap: '4px' }}
                        >
                          <ListItemIcon sx={{ minWidth: 'auto' }}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleUpdateFields(label, result.fieldKey)
                              }
                            >
                              {selectedFieldsData[result.fieldKey]?.includes(
                                label,
                              ) ? (
                                <RemoveCircleOutline
                                  sx={{ color: 'text.primary' }}
                                />
                              ) : (
                                <AddCircleOutline
                                  sx={{ color: 'text.primary' }}
                                />
                              )}
                            </IconButton>
                          </ListItemIcon>
                          <ListItemText
                            primary={result.fieldName}
                            slotProps={{
                              primary: {
                                variant: 'body2',
                              },
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              ))}
            </List>
          )}
        </Box>
      </SidePanel>
    );
  };

  const handleSave = () => {
    // Get the current state directly from the store for dynamic field access
    const state = useListingStore.getState();

    // Get all field keys that have changes (either in current or initial state)
    const allFieldKeys = new Set([
      ...Object.keys(selectedFieldsData),
      ...Object.keys(initialSelectedFieldsData),
    ]);

    // Prepare all field updates
    const fieldsToUpdate: Record<string, any> = {};

    allFieldKeys.forEach((fieldKey) => {
      const currentLabels = selectedFieldsData[fieldKey] || [];
      const initialLabels = initialSelectedFieldsData[fieldKey] || [];
      const currentFieldValue = state.getResourceFieldValue(
        ListingResource.Property,
        fieldKey,
        [],
      );

      // Find labels that were added (in current but not in initial)
      const labelsToAdd = currentLabels.filter(
        (label) => !initialLabels.includes(label),
      );

      // Find labels that were removed (in initial but not in current)
      const labelsToRemove = initialLabels.filter(
        (label) => !currentLabels.includes(label),
      );

      // Only update the field if there are actual changes
      if (labelsToAdd.length > 0 || labelsToRemove.length > 0) {
        // Start with current field value, add new labels, remove unchecked labels
        let updatedFieldValue = [...currentFieldValue];

        // Add new labels
        labelsToAdd.forEach((label) => {
          if (!updatedFieldValue.includes(label)) {
            updatedFieldValue.push(label);
          }
        });

        // Remove unchecked labels
        updatedFieldValue = updatedFieldValue.filter(
          (value) => !labelsToRemove.includes(value),
        );

        fieldsToUpdate[fieldKey] = updatedFieldValue;
      }
    });

    // Update all fields at once
    if (Object.keys(fieldsToUpdate).length > 0) {
      updateField(fieldsToUpdate);
    }

    handleClosePanel();
  };

  return (
    <>
      <CustomDrawer
        title="Objects Detected"
        content={renderObjectPanel()}
        isOpen={isOpen}
        hidePanel={() => {
          handleClosePanel();
        }}
        footer={
          <Box>
            <SaveButton
              onClick={handleSave}
              disabled={
                JSON.stringify(selectedFieldsData) ===
                JSON.stringify(initialSelectedFieldsData)
              }
            />
          </Box>
        }
      />
    </>
  );
};
